import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Toggle "Shipping Protection".
 * - Coché par défaut : la protection est ajoutée automatiquement au panier.
 * - Le client peut la décocher pour la retirer ; son choix (opt-out) est
 *   mémorisé en localStorage pour ne pas la re-ajouter à chaque page.
 * Ajout/retrait via le mécanisme natif Horizon (section morph).
 */
const OPTOUT_KEY = 'shipping_protection_optout';

class CartShippingProtection extends HTMLElement {
  connectedCallback() {
    // Écoute déléguée sur l'élément : le morph de section peut remplacer
    // l'input, un listener posé directement dessus serait alors perdu.
    this.addEventListener('change', this.#onToggle);
    this.#maybeAutoAdd();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onToggle);
  }

  get toggle() {
    return this.querySelector('[data-ref="toggle"]');
  }

  get #optedOut() {
    try {
      return localStorage.getItem(OPTOUT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  set #optedOut(value) {
    try {
      if (value) localStorage.setItem(OPTOUT_KEY, '1');
      else localStorage.removeItem(OPTOUT_KEY);
    } catch (e) {
      /* stockage indisponible */
    }
  }

  #maybeAutoAdd() {
    // Ne pas tenter l'auto-ajout si le produit est indisponible (rupture) :
    // évite le clignotement du toggle. Le bloc reste visible dans tous les cas.
    if (this.dataset.available === 'false') return;
    const inCart = this.dataset.inCart === 'true';
    const defaultOn = this.dataset.defaultOn === 'true';
    if (defaultOn && !inCart && !this.#optedOut) {
      if (this.toggle) this.toggle.checked = true;
      this.#apply(true, true);
    }
  }

  #onToggle = (event) => {
    if (!event.target.matches?.('[data-ref="toggle"]')) return;
    const shouldAdd = this.toggle.checked;
    this.#optedOut = !shouldAdd;
    this.#apply(shouldAdd, false);
  };

  #apply = async (shouldAdd, silent) => {
    const variantId = this.dataset.variantId;
    const sectionId = this.dataset.sectionId || 'cart-drawer-section';
    if (!variantId) return;

    if (!silent) this.toggle.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: shouldAdd ? 'add' : 'remove',
        context: 'shipping-protection',
        lines: [{ merchandiseId: variantId, quantity: shouldAdd ? 1 : 0 }],
        promise: deferred.promise,
      })
    );

    try {
      // Un seul appel : /cart/update.js ajoute (quantité 1) ou retire (0) la
      // variante et renvoie directement le panier complet + les sections.
      const updateUrl = `${Theme.routes.cart_update_url || '/cart/update'}.js`;
      const cart = await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: shouldAdd ? 1 : 0 }, sections: sectionId }),
      }).then((r) => r.json());

      // Shopify renvoie les erreurs (rupture, etc.) en JSON avec un champ status.
      if (cart.status && cart.status >= 400) throw new Error(cart.description || cart.message);
      const response = cart;

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'shipping-protection',
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (this.toggle) this.toggle.checked = !shouldAdd;
    } finally {
      if (this.toggle) this.toggle.disabled = false;
    }
  };
}

if (!customElements.get('cart-shipping-protection')) {
  customElements.define('cart-shipping-protection', CartShippingProtection);
}
